/**
 * @file supabase.js
 * @description Initialisierung und Konfiguration des zentralen Supabase-Clients für ThekenOS.
 *
 * Was Supabase in diesem Projekt ist:
 * Supabase stellt das gesamte Backend für ThekenOS bereit (PostgreSQL-Datenbank, Authentifizierung
 * und Datei-Speicher / Storage).
 *
 * Diese Datei erzeugt den einzigen, globalen Client, der in allen Services verwendet wird.
 * Einstellungen:
 * - `persistSession: true`: Speichert den Login-Status dauerhaft im lokalen Browserspeicher (localStorage).
 * - `autoRefreshToken: true`: Erneuert abgelaufene Sicherheitstoken automatisch im Hintergrund.
 * - `detectSessionInUrl: true`: Erkennt Login-Links und Bestätigungstoken in der Browser-Adresszeile.
 * - `lock`: Verhindert Deadlocks bei parallelen Anfragen im Browser.
 */

import { createClient } from '@supabase/supabase-js';

// URL und anonymer Schlüssel aus Umgebungsvariablen (.env)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error(
    'Supabase-Konfiguration fehlt. Bitte VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY in der .env-Datei setzen.'
  );
}

/**
 * Globaler Supabase-Client für Authentifizierung, Datenbankabfragen und Datei-Operationen.
 */
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    lock: async (_name, _acquireTimeout, fn) => {
      // Direkte Ausführung ohne internen Lock, um Deadlocks im React-Renderzyklus zu vermeiden
      return await fn();
    },
  },
});
